<template>
  <div
    class="signed-layout"
    :class="{ 'signed-layout--full-height': isStartPage && !hideAppChrome }"
  >
  <div
    v-if="!hideAppChrome"
    class="navbar navbar-dark shadow-lg p-0"
  >
    <div class="container-header">
      <div class="navbar-brand-group hstack gap-3 align-items-center">
        <div class="navbar-brand mb-0">
          <a
            href="#"
            class="text-decoration-none"
            @click.prevent="onLogoClick"
          >
            <div class="logo-block">
              <img
                src="@/assets/logo.svg"
                :alt="t('common.logoAlt')"
                id="logo"
              />
              <div class="logo-subtitle">{{ t('productName') }}</div>
            </div>
          </a>
        </div>
        <div
          v-if="link_options?.length > 1"
          ref="serviceDropdownRef"
          class="nav-service-dropdown"
          :class="{ open: serviceMenuOpen }"
        >
          <button
            type="button"
            class="nav-service-toggle"
            :aria-expanded="serviceMenuOpen"
            aria-haspopup="listbox"
            @click="serviceMenuOpen = !serviceMenuOpen"
          >
            <span>{{ currentServiceLabel }}</span>
            <i
              class="bi bi-chevron-down"
              aria-hidden="true"
            />
          </button>
          <div
            class="nav-service-menu"
            role="listbox"
          >
            <button
              v-for="service in availableServices"
              :key="service"
              type="button"
              class="nav-service-option"
              :class="{ active: service === currentService }"
              role="option"
              :aria-selected="service === currentService"
              @click="selectService(service)"
            >
              <i
                class="bi bi-check2"
                aria-hidden="true"
              />
              {{ serviceLabels[service] }}
            </button>
          </div>
        </div>
      </div>
      <!-- Слот всегда в DOM: при появлении поиска лого/кнопки не прыгают -->
      <div class="navbar-home-search-slot">
        <div
          v-if="showHomeSearch"
          class="navbar-home-search"
        >
          <i
            class="bi bi-search navbar-home-search__icon"
            aria-hidden="true"
          />
          <input
            v-model="homeSearch"
            type="search"
            class="navbar-home-search__input"
            :placeholder="t('homeSearchPlaceholder')"
            autocomplete="off"
          />
          <button
            v-if="homeSearch"
            type="button"
            class="navbar-home-search__clear"
            :title="t('common.resetFilters')"
            @click="homeSearch = ''"
          >
            <i
              class="bi bi-x-lg"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
      <div class="hstack gap-2 navbar-header-actions">
        <DebugLocaleSwitcher />
        <div
          v-if="userStore.can('sa')"
          class="debug-service-controls hstack gap-1 align-items-center"
          role="group"
        >
          <DebugPageWidthButton />
        </div>
        <MessagesIndicator
          v-if="!isGlobalTeamUser(user) || user?.has_ltms_role_notifications === true"
        />
        <AuditTrail v-if="userStore.can(['sa', 'audit_trail_view'])" />
        <Tooltip :text="t('common.cabinetTitle')" :hide-delay="200">
          <RouterLink
            :to="{ name: 'profile.item' }"
            class="nav-btn nav-btn--profile"
            :active-class="''"
            :aria-label="t('common.cabinetTitle')"
          >
            <i
              class="bi bi-person-circle"
              aria-hidden="true"
            ></i>
            <span class="nav-btn__label">{{ userDisplayName }}</span>
          </RouterLink>
        </Tooltip>
        <FullscreenButton class="btn btn-sm nav-btn-icon border-0"/>
        <button
          type="button"
          class="btn btn-sm nav-btn-icon border-0"
          @click="logout"
        >
          <i class="bi bi-power"></i>
        </button>
      </div>
    </div>
  </div>
  <div
    v-if="!hideAppChrome"
    class="navbar-scroll-offset"
    aria-hidden="true"
  />
  <nav
    v-if="showHomeActionBar"
    ref="homeActionBarEl"
    class="home-action-bar"
    :aria-label="t('actionBarLabel')"
  >
    <a
      v-for="link in presentationLinks"
      :key="link.path"
      href="#"
      class="home-action-bar__link"
      @click.prevent="handlePresentationTileClick(link)"
    >
      <i
        v-if="link.icon"
        :class="link.icon"
        aria-hidden="true"
      ></i>
      {{ t(link.labelKey) }}
    </a>
  </nav>
  <div
    :class="[
      layoutClasses,
      {
        /* На стартовой без сайдбара верхний воздух режем в CSS (.container-main--no-sidebar) */
        'py-3': !hideAppChrome && showSidebar,
      },
    ]"
  >
    <div :class="{ row: !hideAppChrome }">
      <div
        id="fullscreen-floating"
        class="sticky-top float-end fullscreen-floating-bar hstack gap-2 align-items-center"
        v-if="showFullscreenFloating"
      >
        <FullscreenButton class="btn border-0 btn-icon-square" />
      </div>
      <Sidebar
        class="col-3"
        :class="{ 'sidebar-icon-only': sidebarIconOnly }"
        v-if="showSidebar"
      />
      <main class="main">
        <slot />
      </main>
    </div>
  </div>

  <Teleport to="body">
    <Mod
      v-if="accessRestrictedOpen"
      size="sm"
      @hidden="accessRestrictedOpen = false"
    >
      <template #header>
        <h5 class="modal-title">{{ t('accessRestrictedTitle') }}</h5>
      </template>
      <div class="vstack gap-3">
        <p class="mb-0">{{ t('accessRestrictedBody') }}</p>
        <div class="hstack gap-2 justify-content-end">
          <button
            type="button"
            class="btn btn-primary"
            @click="accessRestrictedOpen = false"
          >
            {{ t('accessRestrictedClose') }}
          </button>
        </div>
      </div>
    </Mod>
  </Teleport>
  </div>
</template>
<style>
#fullscreen-floating {
  width: fit-content;
}

.debug-service-controls {
  align-self: center !important;
  flex-shrink: 0;
  height: fit-content;
}
.navbar-brand-group {
  flex-shrink: 0;
}

.navbar-home-search {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  padding: 0.35rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 2rem;
  background: rgba(255, 255, 255, 0.12);
  box-sizing: border-box;
}

.navbar-home-search__icon {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.9rem;
  margin-right: 0.4rem;
}

.navbar-home-search__input {
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  color: #fff;
  font-size: 0.875rem;
  outline: none;
  box-shadow: none;

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }
}

/* прячем нативный clear у type=search — оставляем только наш крестик */
.navbar-home-search__input::-webkit-search-decoration,
.navbar-home-search__input::-webkit-search-cancel-button {
  -webkit-appearance: none;
  appearance: none;
}

.navbar-home-search__input::-ms-clear {
  display: none;
  width: 0;
  height: 0;
}

.navbar-home-search__input::placeholder {
  color: rgba(255, 255, 255, 0.7);
}

.navbar-home-search__clear {
  border: 0;
  background: transparent;
  color: rgba(255, 255, 255, 0.75);
  padding: 0 0.15rem;
  line-height: 1;
}

.navbar-home-search__clear:hover {
  color: #fff;
}

</style>

<script setup>
import { computed, ref, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { onClickOutside, useResizeObserver } from '@vueuse/core'
import { PAGE_ZOOM_EVENT, cssPxFromVisual, syncPageZoomLayout } from '@/lib/pageZoom'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useApi } from 'soykajs/composables/api'
import { useConfirm } from '@/composables/ui'
import { useInactivityTracker } from '@/composables/itracker'
import { useLockStore } from '@/stores/lock'
import { useUiStore } from '@/stores/ui'
import { useUserStore } from '@/stores/user'
import { isGlobalTeamUser } from '@/i18n/localeStorage.js'
import { resolveMainRoute } from '@/lib/defaultRoute'
import { HOME_ACTION_BAR_LINKS, PRESENTATION_FOLDER_ACCESS_ROLES } from '@/lib/productCard'
import config from '@/config'
import { i18n } from '@/i18n'
import AuditTrail from '@/components/history/AuditTrail.vue'
import DebugLocaleSwitcher from '@/components/debug/DebugLocaleSwitcher.vue'
import DebugPageWidthButton from '@/components/debug/DebugPageWidthButton.vue'
import FullscreenButton from '@/components/fullscreenButton.vue'
import MessagesIndicator from '@/views/messages/indicator.vue'
import Mod from '@/components/mod.vue'
import Sidebar from '@/components/sidebar/sidebar.vue'
import Tooltip from '@/components/tooltip.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const continueConfirm = useConfirm(computed(() => i18n.global.t('common.unsavedContinue')))
const { refresh: getLogoutLink, model: logoutLink } = useApi({ url: 'logout' })
const userStore = useUserStore()
const { user } = storeToRefs(userStore)

/**
 * Клик по логотипу SOYKA (чекбокс «Глобал» / ltms_global_team):
 * — Глобал на стартовой → без действия
 * — Глобал не на стартовой → стартовая (home)
 * — Не глобал на стартовой → Главная (`main`, список SoH), не реестр `/submissions`
 * — Не глобал не на стартовой → стартовая (home)
 */
function onLogoClick() {
  const onStartPage = route.name === 'home'
  const isGlobal = isGlobalTeamUser(user.value)

  if (isGlobal) {
    if (!onStartPage) {
      router.push({ name: 'home' })
    }
    return
  }

  if (onStartPage) {
    // resolveMainRoute: submissions_view → main; иначе первый реестр без SoH.
    router.push(resolveMainRoute(userStore))
    return
  }
  router.push({ name: 'home' })
}
const lockStore = useLockStore()
const { lockKeys } = storeToRefs(lockStore)
const uiStore = useUiStore()
const { fullscreen, returnUrl, sidebarIconOnly, homeSearch } = storeToRefs(uiStore)
// Свёртка меню только на странице досье; иначе (в т.ч. после бага с localStorage) — развернуть.
if (route.name !== 'finder.list') {
  uiStore.sidebarCollapsed = false
}

const isStartPage = computed(() => route.name === 'home')
/** Страница редактора R7 — без шапки и меню модуля. */
const isDocEditor = computed(() => Boolean(route.meta?.hideAppChrome))
/** Скрыть chrome приложения (fullscreen пользователя или редактор R7). */
const hideAppChrome = computed(() => fullscreen.value || isDocEditor.value)
/** Плавающая кнопка выхода из fullscreen — не на странице редактора. */
const showFullscreenFloating = computed(() => fullscreen.value && !isDocEditor.value)
/**
 * Основное меню: скрыто на стартовой и на роутах с meta.sidebar === false
 * (папки презентаций / Local SOPs со стартовой, RSD, pdf-editor и т.п.).
 */
const showSidebar = computed(
  () =>
    !hideAppChrome.value &&
    !isStartPage.value &&
    route.meta?.sidebar !== false
)

/** Поиск и action-bar на стартовой (доступна и по логотипу не-глобалам). */
const showHomeSearch = computed(() => isStartPage.value)

const showHomeActionBar = computed(() => showHomeSearch.value)
const homeActionBarEl = ref(null)
const presentationLinks = HOME_ACTION_BAR_LINKS
const accessRestrictedOpen = ref(false)

/**
 * Низ action-bar → --home-content-top (CSS px до zoom).
 * getBoundingClientRect даёт visual-координаты — делим на getPageZoom().
 */
function syncHomeContentTop() {
  const el = homeActionBarEl.value
  if (!el) {
    document.documentElement.style.removeProperty('--home-content-top')
    return
  }
  const top = Math.max(
    0,
    Math.round(cssPxFromVisual(el.getBoundingClientRect().bottom))
  )
  document.documentElement.style.setProperty('--home-content-top', `${top}px`)
}

useResizeObserver(homeActionBarEl, () => {
  syncHomeContentTop()
})

function onHomeViewportChange() {
  if (showHomeActionBar.value) syncHomeContentTop()
}

watch(
  showHomeActionBar,
  async visible => {
    if (!visible) {
      document.documentElement.style.removeProperty('--home-content-top')
      window.removeEventListener('resize', onHomeViewportChange)
      window.removeEventListener(PAGE_ZOOM_EVENT, onHomeViewportChange)
      return
    }
    await nextTick()
    syncHomeContentTop()
    window.addEventListener('resize', onHomeViewportChange)
    window.addEventListener(PAGE_ZOOM_EVENT, onHomeViewportChange)
  },
  { immediate: true }
)

/**
 * Шапка монтируется после логина — на initPageZoomLimit её ещё нет.
 * Подгоняем ширину fixed-navbar под текущий CSS zoom.
 */
async function syncChromeZoomWidth() {
  await nextTick()
  syncPageZoomLayout()
}

onMounted(() => {
  syncChromeZoomWidth()
  window.addEventListener(PAGE_ZOOM_EVENT, syncChromeZoomWidth)
})

onUnmounted(() => {
  document.documentElement.style.removeProperty('--home-content-top')
  window.removeEventListener('resize', onHomeViewportChange)
  window.removeEventListener(PAGE_ZOOM_EVENT, onHomeViewportChange)
  window.removeEventListener(PAGE_ZOOM_EVENT, syncChromeZoomWidth)
})

const canAccessPresentationFolders = computed(
  () =>
    isGlobalTeamUser(user.value) ||
    PRESENTATION_FOLDER_ACCESS_ROLES.some(role => userStore.can(role))
)

function handlePresentationTileClick(link) {
  if (!canAccessPresentationFolders.value) {
    accessRestrictedOpen.value = true
    return
  }
  if (link.routeName) {
    router.push({ name: link.routeName })
    return
  }
  router.push({ name: 'finder.list', query: { path: link.path } })
}

watch(
  () => route.name,
  (name, prev) => {
    if (name !== 'home' && homeSearch.value) {
      homeSearch.value = ''
    }
    // Досье само сворачивает меню; при уходе в другой раздел — снова развернуть.
    // Просмотрщик/редактор — не «другой раздел»: Досье в KeepAlive, меню остаётся свёрнутым.
    const docShell =
      name === 'soykajs.docview' ||
      name === 'soykajs.docedit' ||
      name === 'soykajs.docedit.mention'
    if (prev === 'finder.list' && name !== 'finder.list' && !docShell) {
      uiStore.sidebarCollapsed = false
    }
  }
)
const layoutClasses = computed(() => ({
  'layout-fullscreen': hideAppChrome.value,
  'container-main': !hideAppChrome.value,
  'container-main--no-sidebar': !hideAppChrome.value && !showSidebar.value,
  'container-fluid': hideAppChrome.value,
}))

const userDisplayName = computed(() => {
  const raw = (user.value?.fio || user.value?.username || '').trim()
  return raw || '—'
})

async function logout(confirmExit = true) {
  if (Object.keys(lockKeys.value).length > 0) {
    if (confirmExit && !(await continueConfirm())) {
      return
    }
    try {
      await lockStore.resetAll()
    } catch (e) {
      console.warn('lockStore.resetAll failed during logout', e)
    }
  }

  const wasSso = user.value?.sso
  returnUrl.value = undefined
  user.value = undefined

  try {
    if (wasSso) {
      await getLogoutLink()
      window.location.assign(logoutLink.value.logout_uri)
    } else {
      await logoutApi()
    }
  } catch (e) {
    console.warn('logoutApi failed', e)
  }
}

useInactivityTracker(() => logout(false))

const { refresh: logoutApi } = useApi({
  url: 'logout',
  method: 'get',
})

const currentService = 'ltms'
const selected = ref(currentService)
const serviceMenuOpen = ref(false)
const serviceDropdownRef = ref(null)

const serviceLabels = Object.freeze({
  ltms: 'LTMS',
  rtms: 'RTMS',
  qems: 'QEMS',
  qdms: 'QDMS',
})

const { model: redirect, refresh: redirectApi } = useApi({
  url: 'sso_redirect',
  method: 'post',
})

const { model: link_options } = useApi({
  url: 'user_services',
  immediate: true,
  pick: d => d.services || [],
})

const availableServices = computed(() => {
  const order = ['rtms', 'ltms', 'qems', 'qdms']
  const allowed = new Set(link_options.value || [])
  return order.filter(service => allowed.has(service))
})

const currentServiceLabel = computed(() => serviceLabels[currentService])

onClickOutside(serviceDropdownRef, () => {
  serviceMenuOpen.value = false
})

/**
 * Базовый URL модуля: runtime/config, иначе тот же origin с подменой поддомена.
 *
 * @param {string} service код сервиса (ltms|rtms|qems|qdms)
 * @returns {string|undefined}
 */
function getHost(service) {
  const key = {
    ltms: 'VITE_LTMS_URL',
    rtms: 'VITE_RTMS_URL',
    qems: 'VITE_QEMS_URL',
    qdms: 'VITE_QDMS_URL',
  }[service]
  if (!key) return undefined
  const direct = window.__RUNTIME__?.[key] || config[key]
  if (direct) return String(direct).replace(/\/$/, '')
  const seed =
    window.__RUNTIME__?.VITE_LTMS_URL ||
    config.VITE_LTMS_URL ||
    window.__RUNTIME__?.VITE_API_URL ||
    config.VITE_API_URL ||
    window.location.origin
  try {
    const u = new URL(seed)
    u.hostname = u.hostname.replace(
      /^(rtms|qems|qdms|ltms|sso|catalogs|docparser)(?=\.)/i,
      service,
    )
    if (!new RegExp(`^${service}(\\.|$)`, 'i').test(u.hostname)) {
      u.hostname = u.hostname.replace(/^[^.:]+/, service)
    }
    return u.origin
  } catch {
    return undefined
  }
}

/**
 * Выбор модуля в шапке: текущий только закрывает меню, другой — редирект.
 *
 * @param {string} service код сервиса (ltms|rtms|qems|qdms)
 * @returns {Promise<void>}
 */
async function selectService(service) {
  serviceMenuOpen.value = false
  if (!service || service === currentService) {
    return
  }
  await navigate(service)
}

/**
 * SSO-переход в другой модуль; url/service передаём явно.
 *
 * @param {string} service код целевого сервиса
 * @returns {Promise<void>}
 */
async function navigate(service) {
  const host = getHost(service)
  if (!service || !host) {
    selected.value = currentService
    return
  }
  selected.value = service
  try {
    await redirectApi({ query: { url: host, service } })
    window.location.href = redirect.value.url
  } catch (error) {
    selected.value = currentService
    throw new Error(t('redirectError'), { cause: error })
  }
}
</script>

<i18n>
{
  "ru": {
    "productName": "Launch Tracking Management System",
    "redirectError": "Ошибка при запросе редиректа",
    "homeSearchPlaceholder": "Search by drug, INN, or AZD code...",
    "actionBarLabel": "Папки презентаций досье",
    "accessRestrictedTitle": "Доступ ограничен",
    "accessRestrictedBody": "У вас нет доступа к этой папке досье.",
    "accessRestrictedClose": "Закрыть",
    "presentations": {
      "regulatoryCommittee": "Committee presentations",
      "regIntel": "Regulatory Intelligence",
      "localSops": "Local SOPs"
    }
  },
  "en": {
    "productName": "Launch Tracking Management System",
    "redirectError": "Redirect request failed",
    "homeSearchPlaceholder": "Search by drug, INN, or AZD code...",
    "actionBarLabel": "Dossier presentation folders",
    "accessRestrictedTitle": "Access restricted",
    "accessRestrictedBody": "You do not have access to this dossier folder.",
    "accessRestrictedClose": "Close",
    "presentations": {
      "regulatoryCommittee": "Committee presentations",
      "regIntel": "Regulatory Intelligence",
      "localSops": "Local SOPs"
    }
  }
}
</i18n>
